/**
 * environmentUtils.ts — Shared helpers for the environment subsystem.
 *
 * @see docs/ims-engine-api/10-feature-environment.md
 */

import * as THREE from 'three';
import type { BackgroundDescriptor, FogDescriptor } from '../api/types';

// Scratch objects reused every frame to avoid GC pressure in the hot path.
const _scratchBgColor  = new THREE.Color();
const _scratchFogColor = new THREE.Color();

/**
 * Apply scene.background based on a BackgroundDescriptor.
 *
 * - 'color'    → scene.background = new THREE.Color(value)
 * - 'hdr-blur' → scene.background = envMap + scene.backgroundBlurriness
 * - 'none'     → scene.background = null
 * - 'dome'     → scene.background = null (dome mesh rendered by DomeBackground)
 */
export function applyBackground(
  scene: THREE.Scene,
  bg: BackgroundDescriptor,
  _envMap: THREE.Texture | null,
): void {
  switch (bg.type) {
    case 'color': {
      _scratchBgColor.set(bg.value);
      if (!(scene.background instanceof THREE.Color) || !scene.background.equals(_scratchBgColor)) {
        scene.background = _scratchBgColor.clone();
      }
      scene.backgroundBlurriness = 0;
      break;
    }
    case 'hdr-blur':
      // hdr-blur background is now handled by drei's <Environment> component
      // in EnvironmentComponent.tsx (avoids PMREM face-seam artefacts).
      // Do NOT set scene.background here — drei manages it via React lifecycle.
      break;
    case 'none':
    case 'dome':
      // 'dome' background mesh is rendered by DomeBackground component.
      scene.background           = null;
      scene.backgroundBlurriness = 0;
      break;
  }
}

/**
 * Apply or clear scene.fog based on a FogDescriptor.
 *
 * - exponential → scene.fog = new THREE.FogExp2(color, density)
 * - null        → scene.fog = null (no fog)
 */
export function applyFog(
  scene: THREE.Scene,
  fog: FogDescriptor | null,
): void {
  if (!fog) {
    if (scene.fog) scene.fog = null;
    return;
  }
  // Avoid re-creating FogExp2 every frame — only update if values differ
  const existing = scene.fog as THREE.FogExp2 | null;
  if (existing && 'density' in existing) {
    _scratchFogColor.set(fog.color);
    if (existing.density !== fog.density || !existing.color.equals(_scratchFogColor)) {
      existing.color.copy(_scratchFogColor);
      existing.density = fog.density;
    }
  } else {
    scene.fog = new THREE.FogExp2(fog.color, fog.density);
  }
}

/**
 * Build full URL for an HDR file from filename + base path.
 */
export function resolveHdrUrl(hdrFile: string, basePath: string): string {
  return `${basePath.replace(/\/$/, '')}/${hdrFile}`;
}
